import React, { useEffect, useState } from 'react';
import { 
  Table, 
  Tag, 
  Space, 
  Button, 
  Dropdown, 
  Menu, 
  Card, 
  Input, 
  Row, 
  Col,
  Typography,
  Tooltip,
  message,
  Popconfirm
} from 'antd';
import { 
  DownOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  MoreOutlined,
  SearchOutlined,
  UserOutlined,
  EditOutlined,
  DeleteOutlined,
  StopOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { getAllUsers, deleteUser } from '../../apis';
import UpdateUser from '../Modals/UpdateUser';
import "../../styles/admin/admin-dashboard.css";
import "../../styles/admin/admin-tables.css";

const { Title } = Typography;
const { Search } = Input;

const UserManagementTable = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getAllUsers();

      if (response && response.length > 0) {
        const formattedUsers = response.map((user, index) => ({
          key: index + 1,
          ...user,
          status: user.status.replace("StatusEnum.", "")
        }));
        setUsers(formattedUsers);
        setFilteredUsers(formattedUsers);
      }
    } catch (error) {
      console.error('Errore nel caricamento utenti:', error);
      message.error('Errore nel caricamento degli utenti');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSearch = (value) => {
    setSearchText(value);
    if (!value) {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user =>
        Object.values(user).some(field =>
          field && field.toString().toLowerCase().includes(value.toLowerCase())
        )
      );
      setFilteredUsers(filtered);
    }
  };

  const statusTag = (status) => {
    let color = '';
    let icon = null;
    let text = '';

    switch (status) {
      case 'approved':
        color = 'success';
        icon = <CheckCircleOutlined />;
        text = 'APPROVATO';
        break;
      case 'pending':
        color = 'warning';
        text = 'IN ATTESA';
        break;
      case 'disabled':
        color = 'error';
        icon = <CloseCircleOutlined />;
        text = 'DISABILITATO';
        break;
      case 'rejected':
        color = 'error';
        text = 'RIFIUTATO';
        break;
      default:
        color = 'default';
        text = status.toUpperCase();
    }

    return (
      <Tag color={color} icon={icon} className="status-tag-active">
        {text}
      </Tag>
    );
  };

  const permissionsTag = (permissions) => {
    if (!permissions || permissions.length === 0) {
      return <Tag color="default">NESSUN PERMESSO</Tag>;
    }
    
    return (
      <Space wrap>
        {permissions.slice(0, 2).map(permission => (
          <Tag color="processing" key={permission}>
            {permission.toUpperCase()}
          </Tag>
        ))}
        {permissions.length > 2 && (
          <Tooltip title={permissions.slice(2).join(', ')}>
            <Tag color="default">+{permissions.length - 2}</Tag>
          </Tooltip>
        )}
      </Space>
    );
  };

  const handleDelete = async (record) => {
    try {
      await deleteUser(record.id);
      message.success('Utente eliminato con successo');
      fetchUsers();
    } catch (error) {
      console.error('Errore nell\'eliminazione dell\'utente:', error);
      message.error('Errore nell\'eliminazione dell\'utente');
    }
  };

  const handleAction = async (action, record) => {
    switch (action) {
      case 'update':
        setSelectedUser(record);
        setOpenUpdateModal(true);
        break;
      case 'delete':
        message.info('Funzione di eliminazione in sviluppo');
        break;
      default:
        break;
    }
  };

  const actionMenu = (record) => (
    <Menu onClick={({ key }) => handleAction(key, record)}>
      <Menu.Item key="update" icon={<EditOutlined />}>
        Modifica
      </Menu.Item>
      <Menu.Item key="disable" icon={<StopOutlined />}>
        Disabilita
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item key="delete" danger icon={<DeleteOutlined />}>
        <Popconfirm
          title="Eliminazione utente"
          description={
            <div className="popconfirm-content">
              <div className="popconfirm-message">Sei sicuro di voler eliminare questo utente?</div>
              <div className="popconfirm-warning">Questa azione non può essere annullata.</div>
            </div>
          }
          onConfirm={() => handleDelete(record)}
          okText="Conferma"
          cancelText="Annulla"
          okButtonProps={{
            className: "admin-form-button-primary"
          }}
          cancelButtonProps={{
            className: "admin-form-button-primary"
          }}
          icon={<DeleteOutlined className="table-action-delete" />}
          placement="left"
        >
          <span className="table-action-delete">Elimina</span>
        </Popconfirm>
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: 'Nome',
      dataIndex: 'first_name',
      key: 'first_name',
      width: 120,
      ellipsis: true,
      render: (text) => (
        <span className="table-text-bold">{text}</span>
      )
    },
    {
      title: 'Cognome',
      dataIndex: 'last_name',
      key: 'last_name',
      width: 120,
      ellipsis: true,
      render: (text) => (
        <span className="table-text-bold">{text}</span>
      )
    },
    {
      title: 'Azienda',
      dataIndex: 'company',
      key: 'company',
      width: 150,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <Tag color="blue">{text}</Tag>
        </Tooltip>
      )
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span className="email-text">{text}</span>
        </Tooltip>
      )
    },
    {
      title: 'Telefono',
      dataIndex: 'number',
      key: 'number',
      width: 130,
      ellipsis: true,
    },
    {
      title: 'Genere',
      dataIndex: 'gender',
      key: 'gender',
      width: 100,
      render: (gender) => {
        const genderMap = {
          'male': 'Maschio',
          'female': 'Femmina',
          'other': 'Altro'
        };
        return genderMap[gender] || gender;
      }
    },
    {
      title: 'Stato',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: status => statusTag(status),
      filters: [
        { text: 'Approvato', value: 'approved' },
        { text: 'In Attesa', value: 'pending' },
        { text: 'Rifiutato', value: 'rejected' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Permessi',
      dataIndex: 'permissions',
      key: 'permissions',
      width: 180,
      render: permissions => permissionsTag(permissions)
    },
    {
      title: 'Azioni',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Dropdown 
          overlay={actionMenu(record)} 
          trigger={['click']}
          placement="bottomRight"
        >
          <Button 
            type="text" 
            icon={<MoreOutlined />}
            className="table-action-button"
          />
        </Dropdown>
      )
    }
  ];

  return (
    <>
      <UpdateUser 
        open={openUpdateModal} 
        onCancel={() => setOpenUpdateModal(false)}
        selectedUser={selectedUser}
        refreshUsers={fetchUsers}
      />
      
      <Card 
        bordered={false}
        className="admin-table-container"
      >
        <Row justify="space-between" align="middle" className="admin-table-header">
          <Col>
            <Title level={3} className="admin-table-title">
              <UserOutlined className="title-icon" />
              Gestione Utenti
            </Title>
            <p className="page-subtitle">
              Gestisci e monitora tutti gli utenti del sistema
            </p>
          </Col>
          <Col>
            <Button 
              type="primary" 
              icon={<ReloadOutlined />}
              onClick={fetchUsers}
              loading={loading}
              className="admin-refresh-button"
            >
              Aggiorna
            </Button>
          </Col>
        </Row>

        <Row gutter={[16, 16]} className="admin-table-actions">
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Cerca utenti..."
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              onSearch={handleSearch}
              onChange={(e) => !e.target.value && handleSearch('')}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <div className="total-counter">
              <span>
                Totale: <strong>{filteredUsers.length}</strong> utenti
              </span>
            </div>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={filteredUsers}
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} di ${total} utenti`,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
          scroll={{ x: 1200 }}
          bordered
          size="middle"
          rowClassName={(record, index) => 
            index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
          }
          className="admin-data-table"
        />
      </Card>
    </>
  );
};

export default function GestionUser() {
  return (
    <div className="admin-dashboard-container">
      <UserManagementTable />
    </div>
  );
}